import { FullScreen } from 'ol/control';
import { Feature, Map, MapBrowserEvent } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import { Draw, Modify, Select, Translate } from 'ol/interaction';
import Projection from 'ol/proj/Projection';
import VectorSource from 'ol/source/Vector';
import {
  FeatureProperties,
  formatArea,
  formatLength,
  getMapStyle,
  ICQLFilter,
  IMapOptions,
  IMapSurveyLayer,
  IMapSurveyVector,
  IMapVector,
  IPopupInfo,
  IRelation,
  IStyleCondition,
  MapStyleSource,
  MapType,
  PopupOverlay,
  toDMS,
} from '@maplix/utils';
import VectorImageLayer from 'ol/layer/VectorImage';
import { cloneDeep, first } from 'lodash';
import { Cluster } from 'ol/source';
import { Heatmap, Layer } from 'ol/layer';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { ISurveyAnswer } from '@maplix/utils';
import { Geometry, MultiLineString, Point, Polygon } from 'ol/geom';
import { transform } from 'ol/proj';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { Coordinate } from 'ol/coordinate';
import { getLength } from 'ol/sphere';
import { FeatureLoader } from 'ol/featureloader';

export class MapClsGeneric {
  public map: Map;

  // draw/select layers
  public lDraw: VectorLayer<VectorSource>;
  public lSelect: VectorLayer<VectorSource>;

  // draw interaction
  public iDraw: Draw;
  public isDrawing: boolean;
  public isModifying: boolean;

  //modify interaction
  public iModify: Modify;
  public iTranslate: Translate;

  //select interaction
  public iSelect: Select;
  public isSelecting: boolean;

  public language: string;

  protected projection: Projection;

  protected isTouchScreen: boolean = matchMedia('(pointer:coarse)').matches;

  private selectedFeatures: { feature: Feature; layer: Layer }[] = [];

  public popupOverlay: PopupOverlay;

  public popupInfo: IPopupInfo;

  public mapType: MapType;

  protected center: BehaviorSubject<Coordinate> = new BehaviorSubject<Coordinate>(undefined);
  public center$: Observable<Coordinate> = this.center.asObservable();

  protected zoom: BehaviorSubject<number> = new BehaviorSubject<number>(undefined);
  public zoom$: Observable<number> = this.zoom.asObservable();

  private vectorSources: { id: string; source: VectorSource }[] = [];

  constructor(
    public target: string,
    protected readonly translate: TranslateService,
    protected readonly http: HttpClient,
    protected readonly environment: any
  ) {}

  public addFullScreenControl(elementId: string = 'fullscreenBox') {
    this.map.addControl(
      new FullScreen({
        source: elementId,
      })
    );
  }

  public setMapOptions(options: IMapOptions) {
    if (!options) {
      return;
    }

    if (options.minZoom || options.minZoom === 0) {
      this.map.getView().setMinZoom(options.minZoom);
    }
    if (options.maxZoom || options.maxZoom === 0) {
      this.map.getView().setMaxZoom(options.maxZoom);
    }
  }

  public setCenter(center: Coordinate) {
    this.map.getView().setCenter(center);
    this.center.next(center);
  }

  public setZoom(zoom: number) {
    this.map.getView().setZoom(zoom);
    this.zoom.next(zoom);
  }

  public removeLastPoint() {
    this.iDraw.removeLastPoint();
  }

  public updateMapSize() {
    if (this.map) {
      this.map.updateSize();
    }
  }

  public removeLastFeature(geomId: string) {
    const features = this.lDraw.getSource().getFeatures();
    if (features.length) {
      const lastFeature = features[features.length - 1];
      if (lastFeature.get('geomId') === geomId) {
        this.lDraw.getSource().removeFeature(lastFeature);
      }
    }
  }

  public setLanguage(language: string) {
    this.language = language;
  }

  public addWFS(
    config: IMapVector | IMapSurveyVector,
    styleSource: MapStyleSource,
    surveyLayer: IMapSurveyLayer | IMapVector,
    styleCondition?: IStyleCondition,
    cqlFilters?: ICQLFilter[],
    zIndex: number = 30000,
    crs: number = 3857
  ): VectorImageLayer<VectorSource> | Heatmap {
    let layer: VectorImageLayer<VectorSource> | Heatmap;
    let featureLoader: FeatureLoader = (extent, resolution, projection, success, failure) => {
      const layerNameSplit = config.typeName.split(':');
      let opts: any = {
        datasource:
          config.type === 'UPLOAD'
            ? layerNameSplit[layerNameSplit.length - 1]
            : crs === 404000
            ? 'SURVEY_RESPONSE_IMAGE_INTERACTION'
            : 'SURVEY_RESPONSE_MAP_INTERACTION',
        bbox: { min_x: extent[0], min_y: extent[1], max_x: extent[2], max_y: extent[3] },
        srid: crs,
      };

      opts = {
        ...opts,
        survey: (config as IMapSurveyVector).survey ?? null,
        categories: (config as IMapSurveyVector).survey
          ? surveyLayer && (surveyLayer as IMapSurveyLayer).interaction
            ? [(surveyLayer as IMapSurveyLayer).interaction]
            : (config as IMapSurveyVector).layers
                .filter((layer) => layer.selectedInModal)
                .map((layer) => layer.interaction)
          : null,
        clauses: styleCondition?.rules
          ? styleCondition.rules.map((rule) => {
              if (rule.type === 'transportMode') {
                return {
                  type: 'TRANSPORT_MODE',
                  operator: 'IN',
                  group_operator: rule.groupOperator,
                  transport_modes: [rule.value],
                  negation: rule.operator === 'not-equal',
                };
              } else if (rule.type === 'question') {
                return {
                  type: 'QUESTION',
                  question: rule.questionid,
                  subquestion: rule.subquestionid,
                  field: 'VALUE',
                  operator: this.mapRuleOperator(rule.operator),
                  value: rule.value,
                  group_operator: rule.groupOperator,
                };
              } else if (rule.type === 'field') {
                return {
                  type: 'FIELD',
                  field: rule.field,
                  operator: this.mapRuleOperator(rule.operator),
                  value: rule.value,
                  group_operator: rule.groupOperator,
                };
              }
            })
          : null,
      };

      this.http
        .post(`${this.environment.api}geo/${config.type === 'UPLOAD' ? 'layer' : 'survey'}/`, opts)
        .toPromise()
        .then((result) => {
          const features = new GeoJSON().readFeatures(result) as Feature<Geometry>[];
          source.addFeatures(
            features.filter(
              (f) =>
                !source
                  .getFeatures()
                  .map((f) => f.getId())
                  .includes(f.getId())
            )
          );
          success(features);
        })
        .catch(() => {
          source.removeLoadedExtent(extent);
          failure();
        });
    };

    const source = new VectorSource({
      format: new GeoJSON(),
      loader: featureLoader,
      strategy: bboxStrategy,
    });

    const layerStyle = getMapStyle(
      config.geomType === 'LineString' && (config as IMapSurveyVector).lineType === 'Route' ? 'Route' : config.geomType,
      styleCondition ? [styleCondition] : surveyLayer.styleConditions,
      styleSource,
      this.map
    );

    if (styleSource === 'heatmap' && surveyLayer.geomType === 'Point') {
      layer = new Heatmap({
        source: source as VectorSource<Point>,
        radius: styleCondition
          ? styleCondition.style.heatmapBlurRadius
          : first(surveyLayer.styleConditions).style.heatmapBlurRadius,
        blur: styleCondition
          ? styleCondition.style.heatmapBlurSize
          : first(surveyLayer.styleConditions).style.heatmapBlurSize,
      });
    } else if (styleSource === 'cluster') {
      layer = new VectorImageLayer({
        source: new Cluster({
          source,
          distance: styleCondition
            ? styleCondition.style.clusterDistance
            : first(surveyLayer.styleConditions).style.clusterDistance,
        }),
        declutter: true,
        style: layerStyle,
      });
    } else {
      layer = new VectorImageLayer({
        source,
        style: layerStyle,
      });
    }

    layer.setZIndex(zIndex);
    layer.setVisible(styleCondition ? styleCondition.selectedInMap : surveyLayer.selectedInMap);
    layer.setMinZoom(config.minZoom ? config.minZoom : 2);
    layer.setMaxZoom(config.maxZoom ? config.maxZoom : 22);
    layer.set('name', styleCondition ? styleCondition.title : surveyLayer.title);
    layer.set('maplixId', styleCondition ? styleCondition.id : surveyLayer.id);
    layer.set('selectable', config.isSelectable);
    layer.set('inLegend', config.showOnLegend);
    layer.set('parentId', config.id);

    return layer;
  }

  private mapRuleOperator(operator: string) {
    const mapper = {
      equals: '=',
      'not-equal': '<>',
      'greater-than-or-equals': '>=',
      'smaller-than-or-equals': '<=',
      'greater-than': '>',
      'smaller-than': '<',
    };

    return mapper[operator];
  }

  setTarget(target: string) {
    this.target = target;
    this.map.setTarget(target);
  }

  public initPopup(element: HTMLElement) {
    this.popupOverlay = new PopupOverlay(element);
    this.map.addOverlay(this.popupOverlay);
  }

  protected async onClickMap(e: MapBrowserEvent<UIEvent>) {
    this.popupInfo = {};
    const features: { feature: Feature; layer: Layer }[] = [];
    this.map.forEachFeatureAtPixel(
      e.pixel,
      (f, layer) => {
        if (layer.get('selectable') && layer.get('visible') && !layer.get('base') && !(layer instanceof Heatmap)) {
          if (!features.map((feature) => feature.feature.getId()).includes(f.getId())) {
            features.push({ feature: f as Feature, layer: layer });
          }
        }
      },
      { hitTolerance: 4 }
    );

    if (!features?.length) {
      this.selectedFeatures = [];
      if (this.popupOverlay) {
        this.popupOverlay.hide();
      }
      return;
    }

    this.selectedFeatures = cloneDeep(features);
    if (!this.popupOverlay) {
      return;
    }

    this.popupOverlay.show(e.coordinate);

    const feature = first(this.selectedFeatures);
    const propertiesObj = feature.feature.get('properties') ?? feature.feature.getProperties();
    let properties = propertiesObj;
    if (typeof propertiesObj === 'string') {
      properties = JSON.parse(propertiesObj);
    }

    this.popupInfo.title =
      feature.layer.get('name') &&
      feature.layer.get('name').find((name) => name.i18n === this.translate.currentLang)?.value;

    const answers = await this.mapFeatureProperties(feature, properties);
    this.popupInfo = {
      ...this.popupInfo,
      features: [answers],
    };
  }

  private async mapFeatureProperties(
    feature: { feature: Feature; layer: Layer },
    properties: any
  ): Promise<FeatureProperties> {
    let answers: ISurveyAnswer[];
    if (feature.feature.get('survey_id')) {
      answers = await this.http
        .get<any>(
          `${this.environment.api}surveys/${feature.feature.get('survey_id')}/responses/${feature.feature.get(
            'response_id'
          )}/answers`
        )
        .pipe(map((response) => (response?.length ? response[0].answers : [])))
        .toPromise();
    }

    const geometry = feature.feature.getGeometry();
    return {
      accordionId: `${feature.feature.getId()}`.replace(/\./g, '_'),
      accordionLabel:
        feature.layer.get('name') &&
        feature.layer.get('name').find((name) => name.i18n === this.translate.currentLang)?.value,
      transportMode: properties.transportMode,
      length: this.mapType === MapType.IMAGE ? undefined : this.getFeatureLength(geometry),
      area: this.mapType === MapType.IMAGE ? undefined : this.getFeatureArea(geometry),
      answers: !answers
        ? undefined
        : this.groupAnswersPerQuestion(answers, feature.feature.get('interaction'), feature.feature.get('survey_id')),
      coordinates: this.mapType === MapType.IMAGE ? undefined : this.getFeatureCoordinates(geometry),
      properties: feature.feature.getProperties(),
    };
  }

  private getFeatureLength(geometry: Geometry) {
    return geometry.getType() === 'LineString'
      ? formatLength(getLength(geometry, { projection: 'EPSG:3857' }))
      : geometry.getType() === 'MultiLineString'
      ? formatLength(
          (geometry as MultiLineString)
            .getLineStrings()
            .map((line) => getLength(line, { projection: 'EPSG:3857' }))
            .reduce((prev, current) => prev + current)
        )
      : undefined;
  }

  private getFeatureArea(geometry: Geometry) {
    return geometry.getType() === 'Polygon' ? formatArea((geometry as Polygon).getArea()) : undefined;
  }

  private getFeatureCoordinates(geometry: Geometry) {
    return geometry.getType() === 'Point'
      ? toDMS(transform((geometry as Point).getCoordinates(), 'EPSG:3857', 'EPSG:4326'))
      : undefined;
  }

  private groupAnswersPerQuestion(
    answers: ISurveyAnswer[],
    interaction: string,
    surveyId: string
  ): { [key: string]: ISurveyAnswer[] } {
    const grouped = {};
    answers
      .filter((answer) =>
        typeof answer.value_selected === 'boolean'
          ? answer.value_selected
          : answer.value || answer.value_order || answer.value_other
      )
      .filter((answer) => answer.interaction === interaction || answer.survey_id !== surveyId)
      .forEach((answer) => {
        if (!grouped[answer.question]) {
          grouped[answer.question] = [answer];
        } else {
          grouped[answer.question].push(answer);
        }
      });
    return grouped;
  }
}
